import type { SpriteIndex } from '@/lib/api/types';

export type ScrubStyle = {
  frame: number;
  backgroundSize: string;
  backgroundPosition: string;
  t_ms: number;
};

/**
 * §2.9 sprite math. `xr` in [0, 0.9999], `width` = rendered width of the media box in px.
 * The tile is scaled so one tile spans `width`; position selects tile (i % cols, floor(i / cols)).
 */
export function spriteStyleAt(idx: SpriteIndex, xr: number, width: number): ScrubStyle {
  const x = xr < 0 ? 0 : xr > 0.9999 ? 0.9999 : xr;
  const i = Math.min(idx.count - 1, Math.floor(x * idx.count));
  const scale = width / idx.tile_w;
  const backgroundSize = `${idx.cols * idx.tile_w * scale}px ${idx.rows * idx.tile_h * scale}px`;
  const backgroundPosition = `-${(i % idx.cols) * width}px -${Math.floor(i / idx.cols) * idx.tile_h * scale}px`;
  const frame = idx.frames[i];
  return { frame: i, backgroundSize, backgroundPosition, t_ms: frame ? frame.t_ms : Math.round(x * idx.duration_ms) };
}

/** Nearest sprite tile for a media time (search results posters, timeline hover). */
export function spriteFrameForTime(idx: SpriteIndex, t_ms: number): number {
  if (idx.count <= 1) return 0;
  // frames are sorted by t_ms
  let lo = 0;
  let hi = idx.count - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const f = idx.frames[mid];
    if (!f || f.t_ms < t_ms) lo = mid + 1;
    else hi = mid;
  }
  const cur = idx.frames[lo];
  const prev = idx.frames[lo - 1];
  if (prev && cur && Math.abs(prev.t_ms - t_ms) < Math.abs(cur.t_ms - t_ms)) return lo - 1;
  return lo;
}

export function spriteStyleForFrame(idx: SpriteIndex, i: number, width: number): Pick<ScrubStyle, 'backgroundSize' | 'backgroundPosition'> {
  const scale = width / idx.tile_w;
  return {
    backgroundSize: `${idx.cols * idx.tile_w * scale}px ${idx.rows * idx.tile_h * scale}px`,
    backgroundPosition: `-${(i % idx.cols) * width}px -${Math.floor(i / idx.cols) * idx.tile_h * scale}px`,
  };
}
